import { createBrowserRouter, useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { JournalsPage } from './pages/journals/JournalsPage';
import { LedgerPage } from './pages/ledger/LedgerPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { DeclarationsPage } from './pages/declarations/DeclarationsPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import DataSharingBenefitsPage from './pages/settings/DataSharingBenefitsPage';
import { FiscalYearsPage } from './pages/fiscal-years/FiscalYearsPage';
import { UsersPage } from './pages/users/UsersPage';
import { ActivityLogsPage } from './pages/users/ActivityLogsPage';
import { OrganizationPage } from './pages/organization/OrganizationPage';
import { ChatPage } from './pages/chat/ChatPage';
import { FixedAssetsPage } from './pages/fixed-assets/FixedAssetsPage';
import { TreasuryPage } from './pages/treasury/TreasuryPage';
import { AnalyticAccountingPage } from './pages/analytic-accounting/AnalyticAccountingPage';
import { RecurringEntriesPage } from './pages/recurring-entries/RecurringEntriesPage';
import { LoginPage } from './pages/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AuditorProtectedRoute } from './components/auth/AuditorProtectedRoute';
import { AuthCallbackPage } from './pages/auth/AuthCallbackPage';
import { NotFoundPage } from './pages/errors/NotFoundPage';

function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
    ? error.message
    : 'Une erreur inattendue s\'est produite';

  const isDomError = error instanceof Error &&
    (error.message.includes('removeChild') || error.message.includes('NotFoundError'));

  useEffect(() => {
    if (isDomError) {
      navigate('/', { replace: true });
    }
  }, [isDomError, navigate]);

  if (isDomError) return null;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4 mx-auto">
          <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
          Oups ! Une erreur s'est produite
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6 text-sm">
          {message}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.history.back()}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Retour
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Recharger
          </button>
        </div>
      </div>
    </div>
  );
}
import { NoOrganizationPage } from './pages/errors/NoOrganizationPage';
import { AuditorClientsPage } from './pages/auditor/AuditorClientsPage';
import { AuditorCompanyRedirect } from './components/auditor/AuditorCompanyRedirect';

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/callback',
    element: <AuthCallbackPage />
  },
  {
    // Route publique : compte authentifié mais sans organisation liée
    path: '/no-organization',
    element: <NoOrganizationPage />
  },
  {
    // Routes réservées aux auditeurs (role === 'audit')
    path: '/auditor',
    element: <AuditorProtectedRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: 'clients',
        element: <AuditorClientsPage />
      },
      {
        // Quand un auditeur ouvre une entreprise, il atterrit dans l'app compta
        // via les mêmes routes standard mais wrappées dans AuditorLayout
        path: 'company/:organizationId',
        element: <AuditorCompanyRedirect />
      },
      {
        // Raccourci : /auditor/* catch-all
        path: '*',
        element: <NotFoundPage />
      },
    ]
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '/',
        element: <DashboardPage />
      },
      {
        path: '/journals',
        element: <JournalsPage />
      },
      {
        path: '/ledger',
        element: <LedgerPage />
      },
      {
        path: '/reports',
        element: <ReportsPage />
      },
      {
        path: '/declarations',
        element: <DeclarationsPage />
      },
      {
        path: '/settings',
        element: <SettingsPage />
      },
      {
        path: '/settings/data-sharing-benefits',
        element: <DataSharingBenefitsPage />
      },
      {
        path: '/fiscal-years',
        element: <FiscalYearsPage />
      },
      {
        path: '/users',
        element: <UsersPage />
      },
      {
        path: '/activity-logs',
        element: <ActivityLogsPage />
      },
      {
        path: '/organization',
        element: <OrganizationPage />
      },
      {
        path: '/chat',
        element: <ChatPage />
      },
      {
        path: '/fixed-assets',
        element: <FixedAssetsPage />
      },
      {
        path: '/treasury',
        element: <TreasuryPage />
      },
      {
        path: '/analytic-accounting',
        element: <AnalyticAccountingPage />
      },
      {
        path: '/recurring-entries',
        element: <RecurringEntriesPage />
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
], {
  future: {
    v7_relativeSplatPath: true
  }
});